import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useUnreadCount } from '@kidshome/api-contract';
import { cn } from '@kidshome/ui';
import { useFeature } from '@/entities/session';
import { Feature } from '@/shared/lib/features';
import { BellIcon } from '@/widgets/app-shell/nav-icons';

/**
 * The notification bell (ADR-0135 §8) — an INFORMATIONAL affordance in every shell header, distinct
 * from the Inbox tab's ACTIONABLE badge (that stays untouched). Links to `/notifications`; shows an
 * unread count badge from {@link useUnreadCount} (focus refetch + a modest visible-only interval).
 * Hidden entirely when the household has the `notifications` feature off (ADR-0073) — the count query
 * is gated on the same flag so it never fires for a disabled feature.
 *
 * `emphasizeTarget` bumps the tap target to ≥44px for the kid persona (small fingers), matching the
 * shell's other kid-sized controls.
 */
export function NotificationBell({
  emphasizeTarget = false,
}: {
  emphasizeTarget?: boolean;
}): React.ReactElement | null {
  const { t } = useTranslation('notifications');
  const hasFeature = useFeature();
  const enabled = hasFeature(Feature.Notifications);
  const { data } = useUnreadCount({ enabled });

  if (!enabled) return null;

  const count = data?.count ?? 0;
  const label =
    count > 0 ? `${t('center.bell')} — ${t('center.unreadBadge', { count })}` : t('center.bell');

  return (
    <Link
      to="/notifications"
      aria-label={label}
      className={cn(
        'relative flex items-center justify-center rounded-md p-2 text-text-muted outline-none transition-colors hover:bg-surface-muted hover:text-text focus-visible:ring-2 focus-visible:ring-ring',
        emphasizeTarget && 'min-h-11 min-w-11',
      )}
    >
      <BellIcon />
      {count > 0 && (
        <span
          aria-hidden="true"
          className="absolute -end-0.5 -top-0.5 inline-flex min-w-4 items-center justify-center rounded-pill bg-primary px-1 text-[0.625rem] font-bold leading-4 text-primary-fg"
        >
          {count > 9 ? '9+' : count}
        </span>
      )}
    </Link>
  );
}
